// src/DrawingApp.js - Mevcut cizim uygulamasi (eski App.js icerigi)
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import CadTopBar from "./components/layout/TopBar/TopBar";
import ProjectSubBar from "./components/layout/TopBar/ProjectSubBar";
import TestPanel from "./components/layout/TestPanel/TestPanel";
import PanoDetayPanel from "./components/layout/PanoDetayPanel/PanoDetayPanel";
import BaslikPanel from "./components/layout/BaslikPanel/BaslikPanel";
import KabloBacasiPanel from "./components/layout/KabloBacasiPanel/KabloBacasiPanel";
import DrawingCanvas from "./components/DrawingCanvas/DrawingCanvas";
import { PanelsProvider } from "./context/PanelsContext";

// SIMPLE SUPPLY POINT TEST IMPORTS (mvp-feeder branch)
import { runSimpleSupplyPointTests } from "./test/index";
import { SimpleSupplyPointIntegrationTest } from "./test/SimpleSupplyPointIntegrationTest";
import { useSelector, useDispatch } from "react-redux";
import BaslikTemplateEditor from "./components/BaslikTemplateEditor/BaslikTemplateEditor";
import { selectEditorState } from "./redux/baslikTemplateSlice";
import EnerjiOdasiEditor from "./components/EnerjiOdasiEditor/EnerjiOdasiEditor";
import { selectIsEnergyRoomOpen } from "./redux/energyRoomSlice";
import { resetAllState, hydrateProject, store as appStore } from "./redux/store";
import { rebuildFloorPlanItemIndex } from "./redux/floorPlanSlice";
import { setProject, setProjectLoading, setShareAccess, setProjectMetadata } from "./redux/projectSlice";
import { createDemoElectricalScene } from "./utils/createDemoScene";
import { restoreTabState, setTabsFromSheets } from "./redux/projectTabsSlice";
import { incrementUpdateCounter } from "./redux/utilsSlice";
import { deserializeProject, toHydratePayload, deserializeScenes, markSnapshotLoaded } from "./modules/persistence";
import { addTesisat } from "./redux/tesisatSlice";
import { loadProject, getProject, resolveShareToken, loadSharedProject } from "./services/projectApi";
import { ensureDefaultBaslik } from "./services/baslikDefault";
import { selectPanelVisibilityForTab } from "./redux/panelVisibilitySlice";
import { setBaslikActiveTab } from "./redux/baslikSlice";
import { hydrateFromShare } from "./redux/editorConfigSlice";
import API_URL from "./config";

// ─── Tesisat migration helper ───────────────────────────────────────────────
// Eski JBZ projelerinde tesisat (priz vb.) scene-objects.json'da. Yeni formatta
// _state/tesisat.json içinde. Hydration sonrasında tesisat slice'ı boş ve
// scene-objects.json'da tesisat tipinde kayıt varsa, Redux'a aktar.
const TESISAT_MIGRATABLE_TYPES = new Set([
  'socket',
  'supplyPoint',
  'mainPanel',
  'electricalPanel',
  'cableShaft',
  'LightSwitch',
  'LightFixture',
  'machine',
  // Buat'lar (junction box noktaları — scene-objects.json'dan restore)
  'socketBuat', 'buatCircle', 'buatSquare',
  'lightingBuat', 'buatLightingCircle', 'buatLightingSquare',
]);

/**
 * Parent socket group'unda originalColor yok — line child'larında. Recursive
 * tarayıp ilk material color veya userData.originalColor değerini bul.
 */
function extractColorFromSerialized(obj) {
  if (!obj) return null;
  // Children içinde ara (line1/line2 gibi material'lı çocuklar)
  if (Array.isArray(obj.children)) {
    for (const child of obj.children) {
      const fromChild = extractColorFromSerialized(child);
      if (fromChild != null) return fromChild;
    }
  }
  // Bu obje material'lı mı?
  if (typeof obj.userData?.originalColor === 'number') return obj.userData.originalColor;
  if (typeof obj.materialData?.color === 'number') return obj.materialData.color;
  return null;
}

/**
 * panelInfo'yu serialized userData'dan reconstruct et. Factory'lere geri verilecek.
 * useData içinde name, details, panelInfo, vb. ayrı yerlerde dağılmış olabilir.
 */
function extractPanelInfoFromSerialized(obj) {
  const ud = obj?.userData || {};
  // Bazı akışlarda userData.panelInfo doğrudan kaydediliyor
  if (ud.panelInfo && typeof ud.panelInfo === 'object') return ud.panelInfo;
  // Aksi halde minimum bilgileri topla
  return {
    name: ud.name || 'Panel',
    installedPower: ud.details?.kuruluGuc ?? 0,
    purposeOfUse: ud.details?.kullanimAmac,
    aboneTuru: ud.details?.aboneTipi,
    color: extractColorFromSerialized(obj) ?? undefined,
  };
}

// Phase A.2: tesisatSlice + wireSlice'ı oku, mevcut item'ları kat planlarının
// tesisatIds/wireIds listelerine re-index et. JBZ load sonrası (migration +
// _state hydration tamamlandıktan sonra) çağırılır.
function rebuildFloorPlanIndexes(dispatch) {
  const state = appStore.getState();
  const tesisatById = state.tesisat?.byId || {};
  const tesisatAllIds = state.tesisat?.allIds || [];
  const wireById = state.wire?.byId || state.wires?.byId || {};
  const wireAllIds = state.wire?.allIds || state.wires?.allIds || [];
  const tesisatItems = tesisatAllIds.map(id => tesisatById[id]).filter(Boolean);
  const wireItems = wireAllIds.map(id => wireById[id]).filter(Boolean);
  // Tesisat ELEKTRİK disiplinine ait → elektrik tab'ının floor plan'larına
  dispatch(rebuildFloorPlanItemIndex({
    tabId: 'elektrik',
    tesisatItems,
    wireItems,
  }));
}

function migrateSceneObjectsToTesisat(sceneObjects, dispatch) {
  if (!sceneObjects || typeof sceneObjects !== 'object') return 0;
  let count = 0;
  // Recursive traversal: yeni format'ta tesisat fpMesh-child nested altında
  // (hoist sonrası documentGroup içinde) olabilir. Eski format'ta scene-direct
  // top-level. Her iki durumda da yakalamak için flatten et.
  const flatten = (entries, acc) => {
    if (!Array.isArray(entries)) return acc;
    for (const e of entries) {
      if (!e) continue;
      acc.push(e);
      if (Array.isArray(e.children) && e.children.length > 0) {
        flatten(e.children, acc);
      }
    }
    return acc;
  };
  // KRİTİK: tesisatSlice hydrationla zaten doğru WORLD position'lı entry'leri
  // restore ediyor. Migrator (recursive flatten) nested entries'i bulunca
  // shallow-merge ile bu WORLD position'ı documentGroup-local hoist
  // pozisyonuyla EZIYOR → mesh yanlış yerde. Çözüm: slice'ta zaten olan
  // id'leri atla. Migrator sadece slice'ta OLMAYAN tesisat için aktif
  // (eski format'tan migrasyon senaryosu).
  const existingIds = new Set(
    Object.keys(appStore.getState().tesisat?.byId || {})
  );
  for (const objects of Object.values(sceneObjects)) {
    if (!Array.isArray(objects)) continue;
    const flatObjects = flatten(objects, []);
    for (const obj of flatObjects) {
      if (!obj?.userData) continue;
      const t = obj.userData.type;
      if (!TESISAT_MIGRATABLE_TYPES.has(t)) continue;
      const id = obj.userData.id;
      if (!id) continue;
      if (existingIds.has(id)) continue;

      // _state/tesisat.json hydration zaten slice'a localPosition yerleştirmiş olabilir.
      // sceneObjects'te localPosition yoksa null bastırmak slice'taki iyi veriyi siler
      // (addTesisat shallow merge). Sadece serialized userData'da localPosition VARSA
      // payload'a koy; yoksa hiç ekleme — slice'taki mevcut değer korunur.
      const serializedLocal = obj.userData?.localPosition;
      const basePayload = {
        id,
        type: t,
        position: obj.position || { x: 0, y: 0, z: 0 },
        rotation: obj.rotation?.z || 0,
        floorPlanId: obj.userData.floorPlanId || null,
        ...(serializedLocal ? { localPosition: serializedLocal } : {}),
      };

      switch (t) {
        case 'socket': {
          // Color: parent group'ta yok, children'daki ilk material color'ı al
          const color = extractColorFromSerialized(obj) ?? 0xff0000;
          dispatch(addTesisat({
            ...basePayload,
            subtype: obj.userData.subtype || 'normal',
            rotateValue: obj.userData.rotate || 0,
            stretchLength: obj.userData.stretchLength || 0,
            power: obj.userData.power || 300,
            color,
            dir: obj.scale?.y === -1 ? -1 : 1,
          }));
          break;
        }

        case 'supplyPoint': {
          dispatch(addTesisat({
            ...basePayload,
            label: obj.userData.name || 'Supply',
          }));
          break;
        }

        case 'mainPanel': {
          dispatch(addTesisat({
            ...basePayload,
            dir: obj.userData.dir || 1,
            panelInfo: extractPanelInfoFromSerialized(obj),
          }));
          break;
        }

        case 'cableShaft': {
          // Kablo bacası userData'sından corner'ları, shaftGroupId, floorPlanId çek
          const ud = obj.userData || {};
          const corner1 = ud.diagonalStart || ud.points?.[0];
          const corner2 = ud.diagonalEnd || ud.points?.[2];
          if (!corner1 || !corner2) break;
          dispatch(addTesisat({
            ...basePayload,
            corner1: { x: corner1.x, y: corner1.y },
            corner2: { x: corner2.x, y: corner2.y },
            shaftGroupId: ud.shaftGroupId || null,
            floorPlanId: ud.floorPlanId || null,
            floorPlanName: ud.floorPlanName || null,
          }));
          break;
        }

        case 'electricalPanel': {
          dispatch(addTesisat({
            ...basePayload,
            dir: obj.userData.dir || 1,
            symmetricalOffset: obj.userData.symmetricalOffset || 0,
            panelKatPlanNo: obj.userData.panelKatPlanNo || 0,
            panelInfo: extractPanelInfoFromSerialized(obj),
          }));
          break;
        }

        case 'LightSwitch': {
          const color = extractColorFromSerialized(obj) ?? 0x0045ff;
          dispatch(addTesisat({
            ...basePayload,
            subtype: obj.userData.subtype || 'normal',
            rotateValue: obj.userData.rotate || 0,
            stretchLength: obj.userData.stretchLength || 0,
            color,
            dir: obj.scale?.y === -1 ? -1 : 1,
          }));
          break;
        }

        case 'LightFixture': {
          const color = extractColorFromSerialized(obj) ?? 0x0045ff;
          dispatch(addTesisat({
            ...basePayload,
            symbol: obj.userData.symbol || 'normal',
            rotateValue: obj.userData.rotate || 0,
            fixtureType: obj.userData.fixtureType || 'B2',
            power: obj.userData.power || '10W',
            lampPower: obj.userData.lampPower ?? 18,
            lampCount: obj.userData.lampCount ?? 1,
            description: obj.userData.description || '',
            color,
            dir: obj.scale?.y === -1 ? -1 : 1,
          }));
          break;
        }

        case 'machine': {
          const color = extractColorFromSerialized(obj) ?? 0xcc5600;
          dispatch(addTesisat({
            ...basePayload,
            machineType: obj.userData.machineType || 'Bulaşık Makinesi',
            power: obj.userData.power || '0',
            angle: obj.userData.angle,
            dir: obj.userData.dir || 1,
            color,
          }));
          break;
        }

        // Buat types (junction boxes at wire connection points)
        case 'socketBuat':
        case 'buatCircle':
        case 'buatSquare':
        case 'lightingBuat':
        case 'buatLightingCircle':
        case 'buatLightingSquare': {
          const color = extractColorFromSerialized(obj) ?? 0x0045ff;
          dispatch(addTesisat({
            ...basePayload,
            buatType: obj.userData.buatType || (t.includes('Square') ? 'square' : 'circle'),
            socketUUID: obj.userData.socketUUID || null,
            wireID: obj.userData.wireID || null,
            panelID: obj.userData.panelID || null,
            color,
          }));
          break;
        }

        default:
          break;
      }
      count += 1;
    }
  }
  return count;
}
const DrawingApp = () => {
  const { projectId: urlProjectId, token: shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [scene, setScene] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [attachmentDxfUrl, setAttachmentDxfUrl] = useState(null);
  const dispatch = useDispatch();
  const loadedProjectIdRef = useRef(null);
  const isLoadingRef = useRef(false); // Prevent concurrent loads
  const scenesRef = useRef({}); // Shared across DrawingCanvas and TopBar for save operations
  const projectState = useSelector((state) => state.project);

  // Drawing app icin overflow hidden
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // simpleSupply: useSelector kaldırıldı — debug window fonksiyonları lazy read kullanır
  const editorState = useSelector(selectEditorState);
  const isEnergyRoomOpen = useSelector(selectIsEnergyRoomOpen);
  // Panel görünürlüğü AKTİF paftaya göre — pafta değişince o paftaya ait paneller
  // açılır, diğerleri gizlenir.
  const activePaftaId = useSelector((s) => s.projectTabs.activeTabId);
  const panelVis = useSelector((s) => selectPanelVisibilityForTab(s, activePaftaId));

  // Başlık verisi pafta-bazlı: aktif paftayı baslikSlice ile senkronla; böylece
  // Başlık paneli + başlık render'ı aktif paftanın kendi verisini kullanır.
  useEffect(() => {
    dispatch(setBaslikActiveTab(activePaftaId || '_default'));
  }, [activePaftaId, dispatch]);

  // ─── Core project load function (used by both autoLoad and handleOpenProject) ───
  const loadProjectIntoEditor = useCallback(async (projectId) => {
    try {
      // Prevent concurrent loads
      if (isLoadingRef.current) {
        return false;
      }
      isLoadingRef.current = true;
      dispatch(setProjectLoading(true));

      // Download file FIRST before clearing anything
      const buffer = await loadProject(projectId);

      // Clear ALL tab scenes
      const allScenes = scenesRef.current || {};
      for (const [tabId, tabScene] of Object.entries(allScenes)) {
        while (tabScene.children.length > 0) {
          const obj = tabScene.children[0];
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
            else obj.material.dispose();
          }
          tabScene.remove(obj);
        }
      }

      // Reset Redux state
      dispatch(resetAllState());

      // Wait for React to process the reset (resets alreadyDrawn.current = false)
      await new Promise(r => setTimeout(r, 100));

      // Deserialize and hydrate
      // eslint-disable-next-line no-console
      console.log('[LOAD:start] path=editor-open');
      const { manifest, slices, sceneObjects } = await deserializeProject(buffer);
      // [SAVE-LOAD-REWRITE] KRİTİK SIRA: markSnapshotLoaded → deserializeScenes
      // → hydrateProject (rebuild hook'lar guard ile skip eder)
      if (sceneObjects && Object.keys(sceneObjects).length > 0) {
        try {
          markSnapshotLoaded();
          deserializeScenes(scenesRef, sceneObjects);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('[LOAD:editor:deserializeScenes-FAILED]', err);
        }
      } else {
        markSnapshotLoaded();
      }
      const payload = toHydratePayload(slices);
      dispatch(hydrateProject(payload));
      await new Promise(r => setTimeout(r, 50));

      // sceneObjects → tesisatSlice migrasyonu HER zaman çalışır (idempotent).
      // Tesisat slice'ı sadece sockets'i tutuyor (interactive akış); paneller/
      // supplyPoint'ler için scene-objects.json tek kaynak. addTesisat reducer
      // aynı id geldiğinde shallow merge yapıyor, dupe oluşmuyor.
      if (sceneObjects) {
        migrateSceneObjectsToTesisat(sceneObjects, dispatch);
      }
      // Phase A.2: tesisat/wire'ları floor plan indekslerine yaz
      rebuildFloorPlanIndexes(dispatch);

      // projectName exists in both old and new format manifests
      const resolvedName = manifest.projectName || 'Proje';
      dispatch(setProject({
        projectId: projectId,
        projectName: resolvedName,
        currentVersion: manifest.history?.length || manifest.version || 0,
      }));
      setProjectName(resolvedName);

      // Fetch project metadata from server (address, parcel info etc.)
      try {
        const projInfo = await getProject(projectId);
        if (projInfo?.metadata) dispatch(setProjectMetadata(projInfo.metadata));
      } catch (_) { /* metadata fetch is non-critical */ }

      // Restore tab state (projectTabs is not wrapped with createResettableReducer,
      // so hydrateProject doesn't reach it — we restore manually)
      if (payload.projectTabs) {
        dispatch(restoreTabState({
          activeTabId: payload.projectTabs.activeTabId,
          enabledPaftaIds: payload.projectTabs.enabledPaftaIds,
          userRole: payload.projectTabs.userRole,
        }));
      }

      // Layout hook'ları tetikle — updateCounter > 0 olmalı ki Kolon Şeması,
      // Yükleme Cetveli, Tek Hat Şeması vb. dokümanlar yeniden oluşturulsun.
      // (updateCounter utils slice'ında, kaydedilmiyor — her yüklemede tetiklenmeli)
      dispatch(incrementUpdateCounter());

      // Proje kendi başlığını taşımıyorsa kullanıcının varsayılan başlığını uygula
      // (hesaba bağlı default — GET /api/me/settings). Fire-and-forget.
      ensureDefaultBaslik();

      loadedProjectIdRef.current = projectId;
      isLoadingRef.current = false;
      dispatch(setProjectLoading(false));
      return true;
    } catch (err) {
      console.error('[JBZ-LOAD] ERROR:', err);
      isLoadingRef.current = false;
      loadedProjectIdRef.current = null; // Allow retry on error
      dispatch(setProjectLoading(false));
      return false;
    }
  }, [dispatch, setProjectName]);

  // ─── Auto-load when URL has projectId (page refresh / direct navigation) ───
  const attachCat = searchParams.get('attachCat');
  const attachFile = searchParams.get('attachFile');
  // ── Salt-okunur ÖN İZLEME modu (iframe) ──
  // Projelerim/Feed sayfaları editörü <iframe src="/app?preview=1&src={jbzUrl}">
  // ile gömer. `preview=1` UI chrome'unu gizler; `src` doğrudan bir .jbz URL'inden
  // yükler (normal projectId/token yüklemesine DOKUNMAZ — ayrı kod yolu).
  const previewSrc = searchParams.get('src');
  const isPreview = searchParams.get('preview') === '1';

  useEffect(() => {
    if (!urlProjectId) { return; }

    // Skip if already loaded or loading
    if (loadedProjectIdRef.current === urlProjectId) { return; }

    // Skip if project was just saved (data already in Redux)
    if (projectState.projectId === urlProjectId) {
      loadedProjectIdRef.current = urlProjectId;
      return;
    }

    // Set ref IMMEDIATELY to prevent double-fire (React 18 StrictMode + async)
    loadedProjectIdRef.current = urlProjectId;

    // If attachment query params exist, handle based on file type
    if (attachCat && attachFile) {
      // JBZ attachments: load as full project (all sheets/paftas)
      if (attachCat === 'jbz' || attachFile.toLowerCase().endsWith('.jbz')) {
        (async () => {
          if (isLoadingRef.current) return;
          isLoadingRef.current = true;
          dispatch(setProjectLoading(true));
          try {
            const token = localStorage.getItem('projabze_token');
            const url = `${API_URL}/api/projects/${urlProjectId}/attachments/${attachCat}/${encodeURIComponent(attachFile)}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('JBZ indirilemedi');
            const buffer = await res.arrayBuffer();

            // Clear ALL tab scenes (same as loadProjectIntoEditor)
            const allScenes = scenesRef.current || {};
            for (const [, tabScene] of Object.entries(allScenes)) {
              while (tabScene.children.length > 0) {
                const obj = tabScene.children[0];
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                  if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
                  else obj.material.dispose();
                }
                tabScene.remove(obj);
              }
            }

            // Reset Redux state
            dispatch(resetAllState());
            await new Promise(r => setTimeout(r, 100));

            // Deserialize
            // eslint-disable-next-line no-console
            console.log('[LOAD:start] path=url');
            const { manifest, slices, sceneObjects } = await deserializeProject(buffer);
            // eslint-disable-next-line no-console
            console.log('[LOAD:url-path:scenesRef-keys-before]', JSON.stringify(Object.keys(scenesRef.current || {})));

            // [SAVE-LOAD-REWRITE] KRİTİK SIRA:
            // 1. markSnapshotLoaded → guard pencere açar (2sn)
            // 2. deserializeScenes → snapshot scene'e yükle
            // 3. hydrateProject → Redux update → rebuild/layout hook'lar tetiklenir
            //    AMA snapshot pencere AÇIK → hook'lar skip → duplicate yok
            if (sceneObjects && Object.keys(sceneObjects).length > 0) {
              try {
                markSnapshotLoaded();
                deserializeScenes(scenesRef, sceneObjects);
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log('[LOAD:url-path:FAILED]', err);
              }
            } else {
              // eslint-disable-next-line no-console
              console.log('[LOAD:url-path:NO-SCENE-DATA]');
              markSnapshotLoaded(); // yine de guard aç — boş bir tab yaratılırsa skip
            }

            const payload = toHydratePayload(slices);
            dispatch(hydrateProject(payload));
            await new Promise(r => setTimeout(r, 50));

            // sceneObjects → tesisatSlice (her zaman, idempotent — bkz. loadProjectIntoEditor)
            if (sceneObjects) {
              migrateSceneObjectsToTesisat(sceneObjects, dispatch);
            }
            // Phase A.2: tesisat/wire'ları floor plan indekslerine yaz
            rebuildFloorPlanIndexes(dispatch);

            const resolvedName = manifest.projectName || 'Proje';
            dispatch(setProject({ projectId: urlProjectId, projectName: resolvedName, currentVersion: manifest.history?.length || manifest.version || 0 }));
            setProjectName(resolvedName);

            try {
              const projInfo = await getProject(urlProjectId);
              if (projInfo?.metadata) dispatch(setProjectMetadata(projInfo.metadata));
            } catch (_) {}

            // Restore tab state — critical for multi-sheet projects
            if (payload.projectTabs) {
              dispatch(restoreTabState({
                activeTabId: payload.projectTabs.activeTabId,
                enabledPaftaIds: payload.projectTabs.enabledPaftaIds,
                userRole: payload.projectTabs.userRole,
              }));
            }

            dispatch(incrementUpdateCounter());
            ensureDefaultBaslik(); // varsayılan başlık (proje kendi başlığını taşımıyorsa)
            loadedProjectIdRef.current = urlProjectId;
            isLoadingRef.current = false;
            dispatch(setProjectLoading(false));
          } catch (err) {
            console.error('[AUTOLOAD] JBZ attachment load error:', err);
            isLoadingRef.current = false;
            loadedProjectIdRef.current = null;
            dispatch(setProjectLoading(false));
          }
        })();
        return;
      }

      // DXF/other attachments: load as DXF overlay
      const token = localStorage.getItem('projabze_token');
      const url = `${API_URL}/api/projects/${urlProjectId}/attachments/${attachCat}/${encodeURIComponent(attachFile)}`;
      // projectIsLoading true→false transition'ı tetikleyelim ki DrawingCanvas
      // 200ms gecikmeli fit-on-load useEffect'i kamerayı içeriğe sıkıştırsın.
      // false dispatch'i TopBar'da loadDxfFile success/error callback'inden gelir.
      dispatch(setProjectLoading(true));
      setAttachmentDxfUrl({ url, token });
      (async () => {
        try {
          const projInfo = await getProject(urlProjectId);
          dispatch(setProject({
            projectId: urlProjectId,
            projectName: projInfo?.name || 'Proje',
            currentVersion: projInfo?.currentVersion || 0,
          }));
          setProjectName(projInfo?.name || 'Proje');
          if (projInfo?.metadata) dispatch(setProjectMetadata(projInfo.metadata));
        } catch (_) { /* non-critical */ }
      })();
      return;
    }

    loadProjectIntoEditor(urlProjectId);
  }, [urlProjectId, projectState.projectId, loadProjectIntoEditor, attachCat, attachFile, dispatch, setProjectName]);

  // ─── Auto-load when URL has share token (/share/:token) ───
  const [shareError, setShareError] = useState(null);
  useEffect(() => {
    if (!shareToken) return;
    if (loadedProjectIdRef.current === `share-${shareToken}`) return;
    loadedProjectIdRef.current = `share-${shareToken}`;

    (async () => {
      try {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        dispatch(setProjectLoading(true));

        // 1. Resolve token → get project info + party config
        const info = await resolveShareToken(shareToken);

        // 2. Download the shared .jbz file
        const buffer = await loadSharedProject(shareToken);

        // 3. Clear scenes
        const allScenes = scenesRef.current || {};
        for (const [, tabScene] of Object.entries(allScenes)) {
          while (tabScene.children.length > 0) {
            const obj = tabScene.children[0];
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
              else obj.material.dispose();
            }
            tabScene.remove(obj);
          }
        }

        // 4. Reset Redux + hydrate
        dispatch(resetAllState());
        await new Promise(r => setTimeout(r, 100));

        // eslint-disable-next-line no-console
        console.log('[LOAD:start] path=share');
        const { manifest, slices, sceneObjects } = await deserializeProject(buffer);
        if (sceneObjects && Object.keys(sceneObjects).length > 0) {
          try {
            markSnapshotLoaded();
            deserializeScenes(scenesRef, sceneObjects);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.log('[LOAD:share-path:FAILED]', err);
          }
        } else {
          markSnapshotLoaded();
        }
        const payload = toHydratePayload(slices);
        dispatch(hydrateProject(payload));
        await new Promise(r => setTimeout(r, 50));

        // sceneObjects → tesisatSlice (her zaman, idempotent — bkz. loadProjectIntoEditor)
        if (sceneObjects) {
          migrateSceneObjectsToTesisat(sceneObjects, dispatch);
        }
        // Phase A.2: tesisat/wire'ları floor plan indekslerine yaz
        rebuildFloorPlanIndexes(dispatch);

        // 5. Set project identity + share access
        const resolvedName = info.projectName || manifest.projectName || 'Paylasilmis Proje';
        dispatch(setProject({
          projectId: info.projectId,
          projectName: resolvedName,
          currentVersion: info.version || 0,
        }));
        dispatch(setShareAccess({
          partyTypeId: info.partyTypeId,
          accessLevel: info.accessLevel,
          token: shareToken,
          partyRole: info.partyType?.name || null,
          // Paydaş bazlı accessConfig (editorAccess, tabs, fileCategories vb.)
          accessConfig: info.stakeholderAccess?.accessConfig || null,
          stakeholderKey: info.stakeholderAccess?.stakeholderKey || null,
        }));
        setProjectName(resolvedName);

        // 5b. Set project metadata from share response
        if (info.projectMetadata) {
          dispatch(setProjectMetadata(info.projectMetadata));
        }

        // 6. Hydrate editor config from share response (party type + sheet types)
        if (info.partyType) {
          dispatch(hydrateFromShare({
            partyType: info.partyType,
            sheetTypes: info.sheetTypes || [],
          }));
        }
        if (info.sheetTypes?.length > 0) {
          dispatch(setTabsFromSheets(info.sheetTypes));
        }

        // 7. Restore tab state
        if (payload.projectTabs) {
          dispatch(restoreTabState({
            activeTabId: payload.projectTabs.activeTabId,
            enabledPaftaIds: payload.projectTabs.enabledPaftaIds,
            userRole: info.partyType?.name || payload.projectTabs.userRole,
          }));
        }

        dispatch(incrementUpdateCounter());
        isLoadingRef.current = false;
        dispatch(setProjectLoading(false));
      } catch (err) {
        console.error('[SHARE] ERROR:', err);
        isLoadingRef.current = false;
        loadedProjectIdRef.current = null;
        dispatch(setProjectLoading(false));
        setShareError(err.message || 'Paylasim linki acilamadi.');
      }
    })();
  }, [shareToken, dispatch, setProjectName]);

  // ─── Salt-okunur ÖN İZLEME: bir .jbz URL'inden yükle (/app?preview=1&src=...) ───
  //   iframe içinde editörü ön izleme aracı olarak kullanmak için. Normal editör
  //   yükleme yollarına (projectId/token/seed) DOKUNMAZ — sadece `src` varken çalışır.
  //   Editörün TAM render pipeline'ını (deserialize + hydrate + tesisat/wire/layout
  //   rebuild) kullanır → ön izleme editörle birebir aynı, asla sapmaz.
  useEffect(() => {
    if (!previewSrc) return;
    const key = `preview-${previewSrc}`;
    if (loadedProjectIdRef.current === key) return;
    loadedProjectIdRef.current = key;
    let cancelled = false;

    (async () => {
      try {
        dispatch(setProjectLoading(true));
        const token = localStorage.getItem('projabze_token');
        const res = await fetch(previewSrc, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`preview fetch ${res.status}`);
        const buffer = await res.arrayBuffer();
        if (cancelled) return;

        // Sahneleri temizle (loadProjectIntoEditor ile aynı temizlik)
        const allScenes = scenesRef.current || {};
        for (const [, tabScene] of Object.entries(allScenes)) {
          while (tabScene.children.length > 0) {
            const obj = tabScene.children[0];
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
              else obj.material.dispose();
            }
            tabScene.remove(obj);
          }
        }
        dispatch(resetAllState());
        await new Promise(r => setTimeout(r, 100));
        if (cancelled) return;

        // KRİTİK SIRA (loadProjectIntoEditor ile aynı): markSnapshotLoaded →
        // deserializeScenes → hydrateProject (rebuild hook'lar guard ile skip eder).
        const { manifest, slices, sceneObjects } = await deserializeProject(buffer);
        if (sceneObjects && Object.keys(sceneObjects).length > 0) {
          try { markSnapshotLoaded(); deserializeScenes(scenesRef, sceneObjects); }
          catch (_) { markSnapshotLoaded(); }
        } else {
          markSnapshotLoaded();
        }
        const payload = toHydratePayload(slices);
        dispatch(hydrateProject(payload));
        await new Promise(r => setTimeout(r, 50));
        if (cancelled) return;

        if (sceneObjects) migrateSceneObjectsToTesisat(sceneObjects, dispatch);
        rebuildFloorPlanIndexes(dispatch);
        dispatch(setProject({
          projectId: null,
          projectName: manifest.projectName || 'Önizleme',
          currentVersion: manifest.history?.length || manifest.version || 0,
        }));
        if (payload.projectTabs) {
          dispatch(restoreTabState({
            activeTabId: payload.projectTabs.activeTabId,
            enabledPaftaIds: payload.projectTabs.enabledPaftaIds,
            userRole: payload.projectTabs.userRole,
          }));
        }
        // Layout hook'ları tetikle (Kolon Şeması, Başlık vb. dokümanlar oluşsun)
        dispatch(incrementUpdateCounter());
        dispatch(setProjectLoading(false));
      } catch (err) {
        if (!cancelled) {
          console.error('[PREVIEW-LOAD] ERROR:', err);
          loadedProjectIdRef.current = null; // hata → tekrar denenebilir
          dispatch(setProjectLoading(false));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [previewSrc, dispatch]);

  // ─── Auto-load seed JBZ (dev/quick-start) — /app?seed=<name> ───
  //   public/demo/<name>.jbz dosyasını indirir ve editöre hydrate eder.
  //   projectId ayarlanmaz → Ctrl+S kendi hesabında yeni proje oluşturur.
  //   Örn: /app?seed=kuvvetli-akim
  const seedName = searchParams.get('seed');
  // ?demo=tesisat → boş sahnede gerçek tesisat komponentlerinden (Box, MainPanel,
  // Panel) demo şema. JBZ yüklemez; createDemoElectricalScene'i tetikler.
  const demoName = searchParams.get('demo');
  useEffect(() => {
    if (!seedName) return;
    if (urlProjectId || shareToken) return; // başka akışla çakışmasın
    const key = `seed-${seedName}`;
    if (loadedProjectIdRef.current === key) return;
    loadedProjectIdRef.current = key;

    (async () => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      dispatch(setProjectLoading(true));
      try {
        // Aynı origin altında public/demo/... dosyası çözülür
        const safeName = seedName.replace(/[^a-zA-Z0-9_\-.]/g, '');
        const url = `${process.env.PUBLIC_URL || ''}/demo/${safeName}.jbz`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Seed bulunamadı (${res.status}): ${url}`);
        const buffer = await res.arrayBuffer();

        const allScenes = scenesRef.current || {};
        for (const [, tabScene] of Object.entries(allScenes)) {
          while (tabScene.children.length > 0) {
            const obj = tabScene.children[0];
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
              else obj.material.dispose();
            }
            tabScene.remove(obj);
          }
        }

        dispatch(resetAllState());
        await new Promise(r => setTimeout(r, 100));

        // eslint-disable-next-line no-console
        console.log('[LOAD:start] path=dragdrop');
        const { manifest, slices, sceneObjects } = await deserializeProject(buffer);
        if (sceneObjects && Object.keys(sceneObjects).length > 0) {
          try {
            markSnapshotLoaded();
            deserializeScenes(scenesRef, sceneObjects);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.log('[LOAD:dragdrop-path:FAILED]', err);
          }
        } else {
          markSnapshotLoaded();
        }
        const payload = toHydratePayload(slices);
        dispatch(hydrateProject(payload));
        await new Promise(r => setTimeout(r, 50));

        // projectId KASITLI olarak set edilmiyor — ilk Ctrl+S yeni proje yaratır
        dispatch(setProject({
          projectId: null,
          projectName: manifest.projectName || `Taslak (${safeName})`,
          currentVersion: 0,
        }));
        setProjectName(manifest.projectName || `Taslak (${safeName})`);

        if (payload.projectTabs) {
          dispatch(restoreTabState({
            activeTabId: payload.projectTabs.activeTabId,
            enabledPaftaIds: payload.projectTabs.enabledPaftaIds,
            userRole: payload.projectTabs.userRole,
          }));
        }
        dispatch(incrementUpdateCounter());
        isLoadingRef.current = false;
        dispatch(setProjectLoading(false));
      } catch (err) {
        console.error('[SEED] ERROR:', err);
        isLoadingRef.current = false;
        loadedProjectIdRef.current = null;
        dispatch(setProjectLoading(false));
        alert(`Seed dosyası yüklenemedi: ${err.message}\n\npublic/demo/${seedName}.jbz mevcut mu?`);
      }
    })();
  }, [seedName, urlProjectId, shareToken, dispatch, setProjectName]);

  // ─── ?demo=tesisat → boş sahnede gerçek tesisat komponentleri ile demo ──
  useEffect(() => {
    if (!demoName) return;
    if (urlProjectId || shareToken || seedName) return;
    if (demoName !== 'tesisat' && demoName !== 'elektrik') return;
    if (!scene) return;
    const key = `demo-${demoName}`;
    if (loadedProjectIdRef.current === key) return;
    loadedProjectIdRef.current = key;

    // setProjectLoading(true)→false transition'ı DrawingCanvas'taki fit-zoom
    // effect'ini tetikler. Componentler eklenince hooks bir frame içinde
    // sahneyi günceller; transition sonrası 200ms gecikme ile fit yapılır.
    dispatch(setProjectLoading(true));
    setProjectName(`Demo: ${demoName}`);
    setTimeout(() => {
      try {
        createDemoElectricalScene(scene, dispatch);
      } catch (err) {
        console.error('[DEMO] hata:', err);
      } finally {
        dispatch(setProjectLoading(false));
      }
    }, 50);
  }, [demoName, scene, urlProjectId, shareToken, seedName, dispatch, setProjectName]);

  // ─── Handle opening a project from the dialog (called by TopBar) ───
  const handleOpenProject = useCallback(async (proj) => {
    const success = await loadProjectIntoEditor(proj.id);
    if (success) {
      navigate(`/app/${proj.id}`, { replace: true });
    } else {
      alert('Proje açma hatası. Dosya sunucuda bulunamadı.');
    }
  }, [navigate, loadProjectIntoEditor]);

  const handleFileSelect = (file) => {
    // DXF isleme kodlari burada olacak
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.runSimpleSupplyPointTests = runSimpleSupplyPointTests;
      window.runSimpleSupplyPointIntegrationTests = () => SimpleSupplyPointIntegrationTest.runAllIntegrationTests();

      window.inspectSimpleSupply = () => {
        return appStore.getState().simpleSupply;
      };

      window.getSimpleSupplyStats = () => {
        const ss = appStore.getState().simpleSupply;
        const stats = {
          componentCount: ss.components.length,
          feederCount: ss.feeders.length,
          hasMetadata: !!ss.metadata,
          lastUpdate: ss.lastCalculationTime
        };
        return stats;
      };

      window.testGroundingWithSimpleSupply = () => {
        return true;
      };

      window.testSchemasWithSimpleSupply = () => {
        return true;
      };
    }
  }, []);

  // Determine if default DXF should load:
  // Only when no projectId in URL and no project loaded in Redux ve seed/demo aktif değilse
  const showDefaultDxf = !isPreview && !urlProjectId && !shareToken && !projectState.projectId && !seedName && !demoName;

  // ─── Save overlay: kayıt sırasında sayfadan ayrılmayı engelle ───
  const isSaving = projectState.isSaving;
  useEffect(() => {
    if (!isSaving) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Kaydetme işlemi devam ediyor. Sayfadan ayrılmak istediğinize emin misiniz?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSaving]);

  return (
    <PanelsProvider>
      <div className="w-screen h-screen overflow-hidden flex flex-col">

        {/* ── Paylaşım Hatası Overlay ── */}
        {shareError && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#1e293b', borderRadius: 12, padding: '32px 48px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid #334155',
              maxWidth: 400, textAlign: 'center',
            }}>
              <div style={{ fontSize: 36 }}>⚠</div>
              <div style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 500 }}>
                Paylasim Linki Hatasi
              </div>
              <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
                {shareError}
              </div>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  marginTop: 8, padding: '8px 24px', background: '#3b82f6',
                  color: '#fff', border: 'none', borderRadius: 6,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Ana Sayfaya Don
              </button>
            </div>
          </div>
        )}

        {/* ── Kaydetme Overlay ── */}
        {isSaving && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#1e293b', borderRadius: 12, padding: '32px 48px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid #334155',
            }}>
              <div style={{
                width: 40, height: 40, border: '3px solid #334155',
                borderTopColor: '#60a5fa', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <div style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 500 }}>
                Proje kaydediliyor...
              </div>
              <div style={{ color: '#94a3b8', fontSize: 13 }}>
                Lütfen sayfadan ayrılmayın
              </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Preview modunda (iframe): dosya/araç menüleri gizli, sadece pafta
            sekmeleri (viewer menüsü) — kullanıcı Mimari/Kuvvetli Akım/... arasında
            geçebilsin. Zoom/pan editörün OrbitControls'u ile (scroll/sürükle). */}
        {isPreview ? (
          <ProjectSubBar projectName={projectName} />
        ) : (
          <CadTopBar scenesRef={scenesRef} scene={scene} projectName={projectName}
            setProjectName={setProjectName}
            onFileSelect={handleFileSelect}
            defaultDxfFile={showDefaultDxf ? "/samples/ornek-5.dxf" : null}
            attachmentDxfInfo={attachmentDxfUrl}
            onOpenProject={handleOpenProject}
          />
        )}
        <div className="flex flex-1 relative" style={{ overflow: 'hidden' }}>
          <DrawingCanvas scenesRef={scenesRef} onSceneReady={setScene}
            preview={isPreview} previewProjectId={isPreview ? urlProjectId : null} />
          {/* Paneller Tesisat menüsünden show/hide edilir. Mount kalır (CSS ile
              gizlenir) → sahne içeriği (başlık/kolon vb.) korunur; sadece UI toggle olur. */}
          <div style={{ display: panelVis.kolonSema ? 'contents' : 'none' }}>
            <TestPanel initialPosition={{ top: 350, left: 1 }} scene={scene} />
          </div>
          <div style={{ display: panelVis.panoDetay ? 'contents' : 'none' }}>
            <PanoDetayPanel scene={scene} />
          </div>
          <div style={{ display: panelVis.baslik ? 'contents' : 'none' }}>
            <BaslikPanel scene={scene} />
          </div>
          <div style={{ display: panelVis.kabloBacasi ? 'contents' : 'none' }}>
            <KabloBacasiPanel />
          </div>
        </div>
      </div>
      {editorState.isOpen && <BaslikTemplateEditor />}
      {isEnergyRoomOpen && <EnerjiOdasiEditor />}
    </PanelsProvider>
  );
};

export default DrawingApp;
